import React, { useState } from 'react';

// Agent quick actions for the post closing texts. Each text can be edited before sending, and the agent
// can pick one client when the deal has more than one contact. Sending opens the text with the message filled in.

const MESSAGES = {
  twoDay: (g) => `Hey ${g}! I've been thinking about you and hoping the first few days in your new home have felt wonderful. Congratulations again, and I'd love to hear how it's going.`,
  twoWeek: (g) => `Hi ${g}, it's been about two weeks since you moved in, and I've been wondering how you're settling in. Are you unpacked yet, or have you started getting pictures on the walls? I'd really love to hear how things are going and how I can help.`,
  oneMonth: (g) => `Hi ${g}, can you believe it's been a month already? I hope the new place is starting to feel like home. Have you had a chance to meet any of your neighbors? I'd love to hear how you're doing.`,
  threeMonth: (g) => `Hi ${g}, I've been thinking about you and hoping these first few months have been wonderful. How is your home working out for you? Just reply to this text anytime, I'm always glad to hear from you.`,
  sixMonth: (g) => `Hi ${g}, it's hard to believe six months have already gone by! I hope you're loving your home. I'd really love to hear how everything is going, so just reply to this text whenever you have a moment.`,
  anniversary: (g, n) => `Hi ${g}, happy home anniversary! It's been ${n} year${n === 1 ? '' : 's'} since you closed on your home, and I've been thinking about you. I hope it's still treating you well. I'd love to hear from you.`,
};

// Which message goes with which task. Returns null for tasks that have no text.
const messageFor = (taskName, greeting) => {
  const name = taskName || '';
  if (/2 Day Up Check In/i.test(name)) return MESSAGES.twoDay(greeting);
  if (/2 Week Post Closing Call/i.test(name)) return MESSAGES.twoWeek(greeting);
  if (/1 Month Post Closing Follow Up/i.test(name)) return MESSAGES.oneMonth(greeting);
  if (/3 Month Post Close Follow Up/i.test(name)) return MESSAGES.threeMonth(greeting);
  if (/6 Month Follow Up/i.test(name)) return MESSAGES.sixMonth(greeting);
  const year = name.match(/^Home Anniversary Year (\d+)/i);
  if (year) return MESSAGES.anniversary(greeting, Number(year[1]));
  return null;
};

export const isFollowUpTextTask = (name) => messageFor(name, 'there') !== null;

const digitsOf = (p) => String(p || '').replace(/[^0-9+]/g, '');
const joinNames = (names) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || 'there'));

export default function FollowUpTextQuickEntry({ task, deal, onSent }) {
  const taskName = task?.name || task?.title || '';
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('all');
  const [body, setBody] = useState('');

  if (!isFollowUpTextTask(taskName)) return null;

  const contacts = [
    { name: deal?.firstName || '', phone: deal?.phone || '' },
    ...((deal?.additionalContacts || []).filter(c => c && (c.firstName || c.phone)).map(c => ({ name: c.firstName || '', phone: c.phone || '' }))),
  ].filter(c => c.name || c.phone);

  const chosen = pick === 'all' ? contacts : contacts.filter((_, i) => String(i) === String(pick));
  const numbers = chosen.map(c => digitsOf(c.phone)).filter(Boolean);
  const greeting = joinNames(chosen.map(c => c.name).filter(Boolean));

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer', marginRight: 8 };
  const btnCancel = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };

  const toggle = () => {
    if (open) { setOpen(false); return; }
    setBody(messageFor(taskName, greeting) || '');
    setOpen(true);
  };

  const choose = (value) => {
    setPick(value);
    const picked = value === 'all' ? contacts : contacts.filter((_, i) => String(i) === String(value));
    setBody(messageFor(taskName, joinNames(picked.map(c => c.name).filter(Boolean))) || '');
  };

  const done = () => { if (onSent) onSent(); };

  return (
    <div style={{ marginTop: 8, padding: 10, border: '1px solid #E5E0D3', borderRadius: 6, background: '#FFFFFF' }}>
      <div style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 600, marginBottom: 6 }}>
        Text to: {contacts.length ? contacts.map(c => c.name || c.phone).join(', ') : 'No contacts saved on this deal'}
      </div>
      {contacts.length > 1 && (
        <select value={pick} onChange={(e) => choose(e.target.value)} style={{ padding: 6, fontSize: 13, marginBottom: 8 }}>
          <option value="all">Everyone on this deal</option>
          {contacts.map((c, i) => (
            <option key={i} value={String(i)}>{c.name || c.phone}</option>
          ))}
        </select>
      )}
      <button type="button" onClick={toggle} style={btn}>{open ? 'Hide text' : 'Review and send text'}</button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} style={{ width: '100%', boxSizing: 'border-box', padding: 8, fontSize: 13 }} />
          <div style={{ marginTop: 8 }}>
            {numbers.length > 0 && <a href={`sms:${numbers.join(',')}?body=${encodeURIComponent(body)}`} onClick={done} style={btn}>Send text</a>}
            <button type="button" onClick={() => setOpen(false)} style={btnCancel}>Cancel</button>
          </div>
          {numbers.length === 0 && <div style={{ fontSize: 12, color: '#5B6270', marginTop: 6 }}>No phone number saved for the clients on this deal.</div>}
        </div>
      )}
    </div>
  );
}
